import { useMemo, useSyncExternalStore } from 'react';
import useAddChapterItem from '../../hooks/useAddChapterItem';
import { useAddScene } from '../../TimelineSegments/TimelineScenes/useAddScene';
import { AddMenuItem, MenuItemGroup, GroupedMenuItems } from './types';
import { useMotionGraphics } from '../../../ClipAssets/MotionGraphics/useMotionGraphics';
import { useZoomEffectUpdate } from '@/Pages/Clip/ClipPlayer/ClipCustomizer/pocAIEdits/hooks/useZoomEffectUpdate';
import { useCalloutCaptionAdd } from '@/Pages/Clip/ClipPlayer/ClipAssets/CalloutCaptions/useCalloutCaptionAdd';
import { useImageOverlay } from '@/Pages/Clip/ClipPlayer/ClipAssets/ImageOverlay/useImageOverlay';
import { useVideoOverlay } from '@/Pages/Clip/ClipPlayer/ClipAssets/VideoOverlay/useVideoOverlay';
import { useAudioOverlay } from '@/Pages/Clip/ClipPlayer/ClipAssets/AudioOverlay/useAudioOverlay';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const useAddMenuItems = (): GroupedMenuItems => {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isAIEditingEnabled = featureFlags[FeatureFlagKeys.Use_CL_AI_Edits_Beta];

  const addChapterItem = useAddChapterItem();
  const { addSceneMenuItem } = useAddScene();
  const { addZoomEffectMenuItem } = useZoomEffectUpdate();
  const { addCalloutCaptionMenuItem } = useCalloutCaptionAdd();
  const { addImageOverlayMenuItem } = useImageOverlay({});
  const { addVideoOverlayMenuItem } = useVideoOverlay({});
  const { addAudioOverlayMenuItem } = useAudioOverlay();
  const { addMotionGraphicsMenuItem } = useMotionGraphics();

  return useMemo(() => {
    // Timeline structure items
    const timelineItems = [addChapterItem, addSceneMenuItem].filter((item): item is AddMenuItem => Boolean(item));

    // Content and effects items
    const contentItems = [
      isAIEditingEnabled && addZoomEffectMenuItem,
      isAIEditingEnabled && addCalloutCaptionMenuItem,
      addImageOverlayMenuItem,
      addVideoOverlayMenuItem,
      addAudioOverlayMenuItem,
      isAIEditingEnabled && addMotionGraphicsMenuItem
    ].filter((item): item is AddMenuItem => Boolean(item));

    const groups: MenuItemGroup[] = [
      { name: 'timeline', items: timelineItems },
      { name: 'content', items: contentItems }
    ].filter(group => group.items.length > 0);

    return { groups };
  }, [
    addChapterItem,
    addSceneMenuItem,
    isAIEditingEnabled,
    addZoomEffectMenuItem,
    addCalloutCaptionMenuItem,
    addImageOverlayMenuItem,
    addVideoOverlayMenuItem,
    addAudioOverlayMenuItem,
    addMotionGraphicsMenuItem
  ]);
};

export default useAddMenuItems;
